import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Package, Filter, RefreshCw, AlertCircle, AlertTriangle, FileSpreadsheet, Download, Loader2, CheckSquare, Square, Calendar, Building2, Eye, Edit, ClipboardCheck, Trash2, User } from 'lucide-react';
import POCard from './po/POCard';
import POTabDialog from './po/POTabDialog';
import POViewDialog from './po/POViewDialog';
import GRNDialog from './po/GRNDialog';
import ReRaiseDialog from '@/components/po/ReRaiseDialog';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  waiting_approval: 'bg-amber-100 text-amber-800',
  resubmitted: 'bg-purple-100 text-purple-800',
  sent: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-emerald-100 text-emerald-800',
  short_closed: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-red-100 text-red-800',
};

function PurchaseOrders() {
  const { canCreate: canCreateModule, canEdit: canEditModule, canDelete: canDeleteModule } = useModulePermissions();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Bulk selection states
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('separate_sheets');
  
  // User access state
  const [userAccess, setUserAccess] = useState(null);
  
  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  
  const [editingPO, setEditingPO] = useState(null);
  const [viewPO, setViewPO] = useState(null);
  const [grnPO, setGrnPO] = useState(null);
  const [reRaiseOpen, setReRaiseOpen] = useState(false);
  const [reRaisePO, setReRaisePO] = useState(null);
  
  useEffect(() => {
    fetchAllData();
    fetchUserAccess();
  }, []);
  
  async function fetchUserAccess() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API + '/po/user-access', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setUserAccess(response.data);
    } catch {
      // User-access probe failed — permissions will default-deny on render.
    }
  }
  
  async function fetchAllData() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      const [ordersRes, suppliersRes, productsRes] = await Promise.all([
        axios.get(API + '/purchase-orders', { headers }),
        axios.get(API + '/suppliers', { headers }),
        axios.get(API + '/products', { headers }),
      ]);
      
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setSelectedPOs([]); // Clear selection on refresh
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }
  
  function canPerformAction(action) {
    if (!userAccess) return true;
    if (userAccess.is_admin) return true;
    return userAccess.action_permissions[action] === true;
  }
  
  function canBulkExport() {
    if (!userAccess) return true;
    return userAccess.is_admin || userAccess.role === 'manager' || ['admin', 'manager'].includes(userAccess.user_role);
  }
  
  function handleCreate() {
    if (!canPerformAction('create')) {
      toast.error("You don't have permission to create purchase orders");
      return;
    }
    setEditingPO(null);
    setFormOpen(true);
  }
  
  function handleEdit(po) {
    if (!canPerformAction('edit')) {
      toast.error("You don't have permission to edit purchase orders");
      return;
    }
    setEditingPO(po);
    setFormOpen(true);
  }
  
  function handleView(po) {
    setViewPO(po);
    setViewOpen(true);
  }
  
  function handleReceive(po) {
    if (!canPerformAction('receive_grn')) {
      toast.error("You don't have permission to receive stock");
      return;
    }
    setGrnPO(po);
    setGrnOpen(true);
  }

  async function handleReRaise(po) {
    // Open the preview dialog (Cancel / Proceed). The actual POST happens inside the dialog.
    setReRaisePO(po);
    setReRaiseOpen(true);
  }

  async function handlePendingReport() {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (supplierFilter && supplierFilter !== 'all') params.append('supplier_id', supplierFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const qs = params.toString();
      const res = await axios.get(
        `${API}/reports/po-pending-items.xlsx${qs ? '?' + qs : ''}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `pending_items_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success('Pending Items Report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download pending items report');
    }
  }

  async function handleQuickSubmit(po) {
    if (!window.confirm(`Submit ${po.po_number} for approval?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/purchase-orders/${po.id}/submit`, { comment: null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${po.po_number} submitted for approval successfully!`);
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit for approval');
    }
  }
  
  function handleFormSuccess() {
    setFormOpen(false);
    setEditingPO(null);
    fetchAllData();
  }
  
  function handleGrnSuccess() {
    setGrnOpen(false);
    setGrnPO(null);
    fetchAllData();
  }
  
  // Bulk selection handlers
  function togglePOSelection(poId) {
    if (selectedPOs.includes(poId)) {
      setSelectedPOs(selectedPOs.filter(id => id !== poId));
    } else {
      setSelectedPOs([...selectedPOs, poId]);
    }
  }
  
  function toggleSelectAll() {
    if (selectedPOs.length === filteredOrders.length) {
      setSelectedPOs([]);
    } else {
      setSelectedPOs(filteredOrders.map(o => o.id));
    }
  }

  async function handleDeleteSelectedPOs() {
    if (selectedPOs.length === 0) return;
    if (!window.confirm(`Delete ${selectedPOs.length} Purchase Order(s)? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(API + '/purchase-orders/bulk-delete', { ids: selectedPOs }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success(`${selectedPOs.length} PO(s) deleted`);
      setSelectedPOs([]);
      fetchAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  }

  async function handleDeleteSinglePO(poId) {
    if (!window.confirm('Delete this Purchase Order?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(API + '/purchase-orders/' + poId, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('PO deleted');
      fetchAllData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  }
  
  async function handleBulkExport() {
    if (selectedPOs.length === 0) {
      toast.error('Please select at least one PO to export');
      return;
    }
    
    setBulkExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API + '/purchase-orders/bulk-export-excel',
        {
          po_ids: selectedPOs,
          export_format: exportFormat
        },
        {
          headers: { Authorization: 'Bearer ' + token },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `PO_Bulk_Export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedPOs.length} PO(s) successfully`);
      setSelectedPOs([]); // Clear selection after export
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export POs');
    } finally {
      setBulkExporting(false);
    }
  }
  
  async function handleFilteredExport() {
    setBulkExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API + '/purchase-orders/bulk-export-excel',
        {
          supplier_id: supplierFilter !== 'all' ? supplierFilter : null,
          status: statusFilter !== 'all' ? statusFilter : null,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          export_format: exportFormat
        },
        {
          headers: { Authorization: 'Bearer ' + token },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `PO_Filtered_Export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Filtered POs exported successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export filtered POs');
    } finally {
      setBulkExporting(false);
    }
  }
  
  // Apply filters
  const filteredOrders = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (supplierFilter !== 'all' && o.supplier_id !== supplierFilter) return false;
    if (dateFrom && o.po_date < dateFrom) return false;
    if (dateTo && o.po_date > dateTo) return false;
    return true;
  });
  
  const allSelected = filteredOrders.length > 0 && selectedPOs.length === filteredOrders.length;
  const someSelected = selectedPOs.length > 0 && selectedPOs.length < filteredOrders.length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-600">Loading Purchase Orders...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">Purchase Orders</h1>
          <p className="text-slate-600 mt-1">Create and manage purchase orders</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAllData} variant="outline" size="sm" className="gap-1">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canPerformAction('create') && canCreateModule('purchase_order') && (
            <Button onClick={handleCreate} className="gap-2" data-testid="create-po-btn">
              <Plus size={18} />
              Create PO
            </Button>
          )}
        </div>
      </div>
      
      {/* User Role Badge */}
      {userAccess && !userAccess.is_admin && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 text-blue-600" />
          <span>You are logged in as <Badge className="ml-1 capitalize">{userAccess.role}</Badge></span>
          <span className="text-gray-500">- Some features may be limited based on your role.</span>
        </div>
      )}
      
      {/* Filters Section */}
      <Card className="border-blue-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters & Export</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="waiting_approval">Waiting Approval</SelectItem>
                  <SelectItem value="resubmitted">Resubmitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected (Needs Action)</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partially_received">Partially Received</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="short_closed">Short Closed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Supplier</Label>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger className="mt-1" data-testid="supplier-filter">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs text-gray-500">Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="separate_sheets">Separate Sheets</SelectItem>
                  <SelectItem value="consolidated">Consolidated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Bulk Export Section */}
          {canBulkExport() && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    className="data-[state=checked]:bg-blue-600"
                  />
                  <span className="text-sm text-gray-600">
                    {selectedPOs.length > 0 ? `${selectedPOs.length} selected` : 'Select All'}
                  </span>
                </div>
                <Badge variant="outline" className="gap-1">
                  <Package className="w-3 h-3" />
                  {filteredOrders.length} Orders
                </Badge>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePendingReport}
                  disabled={bulkExporting}
                  className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                  title="Download a consolidated Excel of all pending (unreceived) items across the currently filtered POs"
                  data-testid="pending-report-btn"
                >
                  <Download className="w-4 h-4" />
                  Pending Items Report
                </Button>
                <CsvDownloadButton onDownload={handlePendingReport} label="Pending CSV" disabled={bulkExporting} testId="pending-report-csv" />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFilteredExport}
                  disabled={bulkExporting || filteredOrders.length === 0}
                  className="gap-2"
                >
                  {bulkExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Export Filtered ({filteredOrders.length})
                </Button>
                <CsvDownloadButton onDownload={handleFilteredExport} testId="po-filtered-export-csv" disabled={bulkExporting || filteredOrders.length === 0} />
                
                <Button
                  size="sm"
                  onClick={handleBulkExport}
                  disabled={bulkExporting || selectedPOs.length === 0}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="bulk-export-btn"
                >
                  {bulkExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                  Bulk Export Selected ({selectedPOs.length})
                </Button>
                <CsvDownloadButton onDownload={handleBulkExport} testId="bulk-export-csv-btn" disabled={bulkExporting || selectedPOs.length === 0} />
                
                {canDeleteModule('purchase_order') && selectedPOs.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeleteSelectedPOs}
                    className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="delete-selected-po"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected ({selectedPOs.length})
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Orders List with Checkboxes */}
      <div className="space-y-3">
        {filteredOrders.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No purchase orders found</h3>
              <p className="text-gray-500 mt-1">
                {canPerformAction('create') 
                  ? 'Create your first purchase order to get started'
                  : 'No orders match your current filters'}
              </p>
            </CardContent>
          </Card>
        )}
        
        {filteredOrders.map(order => {
          const isSelected = selectedPOs.includes(order.id);
          const statusClass = STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800';
          // Allow editing draft POs normally
          const canEditNormal = order.status === 'draft';
          // Rejected POs get special Edit & Resubmit treatment
          const canEditRejected = order.status === 'rejected';
          // Only approved POs can be received (approval workflow enforced)
          const canReceive = ['approved', 'partially_received'].includes(order.status);
          const isResubmitted = order.status === 'resubmitted';
          const canReRaise = order.status === 'partially_received' && canCreateModule('purchase_order');
          
          return (
            <Card
              key={order.id}
              data-testid={'po-card-' + order.id}
              className={`hover:shadow-md transition-all ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''} ${canEditRejected ? 'border-red-200' : ''}`}
            >
              <CardContent className="p-4">
                {/* Rejection Banner for Rejected POs */}
                {canEditRejected && order.rejection_reason && (
                  <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-md flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <span className="font-semibold text-red-800">Rejected: </span>
                      <span className="text-red-700">{order.rejection_reason}</span>
                      {order.rejected_by_name && (
                        <span className="text-red-600 text-xs ml-2">by {order.rejected_by_name}</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Resubmitted Tag */}
                {isResubmitted && (
                  <div className="mb-3 p-2 bg-purple-100 border border-purple-300 rounded-md flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-purple-800 font-medium">Resubmitted for Approval</span>
                  </div>
                )}
                
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  {canBulkExport() && (
                    <div className="pt-1">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => togglePOSelection(order.id)}
                        className="data-[state=checked]:bg-blue-600"
                        data-testid={'select-po-' + order.id}
                      />
                    </div>
                  )}
                  
                  {/* PO Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-primary">{order.po_number}</h3>
                      <Badge className={statusClass}>
                        {order.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 size={14} />
                        {order.supplier_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {order.po_date}
                      </span>
                      <span className="text-gray-400">
                        {order.items?.length || 0} items
                      </span>
                      {order.created_by_name && (
                        <span className="flex items-center gap-1 text-slate-500" data-testid={'po-created-by-' + order.id}>
                          <User size={14} /> {order.created_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Amount & Actions */}
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-bold text-green-600 mb-2">
                      ₹{(order.total_amount || 0).toFixed(2)}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleView(order)} data-testid={'view-po-' + order.id}>
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                      {canEditNormal && canEditModule('purchase_order') && (
                        <Button variant="outline" size="sm" onClick={() => handleEdit(order)} data-testid={'edit-po-' + order.id}>
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                      )}
                      {canEditNormal && canEditModule('purchase_order') && (
                        <Button size="sm" onClick={() => handleQuickSubmit(order)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={'submit-po-' + order.id}>
                          <ClipboardCheck size={14} className="mr-1" /> Submit
                        </Button>
                      )}
                      {canEditRejected && canEditModule('purchase_order') && (
                        <Button size="sm" onClick={() => handleEdit(order)} className="bg-orange-500 hover:bg-orange-600" data-testid={'edit-resubmit-' + order.id}>
                          <Edit size={14} className="mr-1" /> Edit & Resubmit
                        </Button>
                      )}
                      {canReceive && (
                        <Button size="sm" onClick={() => handleReceive(order)} data-testid={'receive-po-' + order.id}>
                          <Package size={14} className="mr-1" /> Receive
                        </Button>
                      )}
                      {canReRaise && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReRaise(order)}
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          title="Create a new draft PO covering the unreceived items and short-close this one"
                          data-testid={'reraise-po-' + order.id}
                        >
                          <RefreshCw size={14} className="mr-1" /> Re-Raise
                        </Button>
                      )}
                      {order.status === 'short_closed' && order.re_raised_as_po_number && (
                        <span className="text-xs text-slate-500 italic self-center" data-testid={'reraise-link-' + order.id}>
                          → {order.re_raised_as_po_number}
                        </span>
                      )}
                      {canDeleteModule('purchase_order') && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteSinglePO(order.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={'delete-po-' + order.id}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Tab-based PO Form Dialog */}
      <POTabDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editingPO={editingPO}
        suppliers={suppliers}
        products={products}
        onSuccess={handleFormSuccess}
        userAccess={userAccess}
      />
      
      {/* GRN Dialog (for quick receiving from list) */}
      <GRNDialog
        open={grnOpen}
        onOpenChange={setGrnOpen}
        po={grnPO}
        onSuccess={handleGrnSuccess}
      />
      
      {/* PO View Dialog with Actions */}
      <POViewDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        po={viewPO}
        userAccess={userAccess}
        onRefresh={fetchAllData}
      />
      
      {/* Re-Raise Preview Dialog (Cancel / Proceed + Download Pending Excel) */}
      <ReRaiseDialog
        open={reRaiseOpen}
        onOpenChange={setReRaiseOpen}
        sourcePo={reRaisePO}
        onSuccess={fetchAllData}
      />
    </div>
  );
}

export default PurchaseOrders;
